import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, ArrowUpRight } from 'lucide-react';
import { useLang } from '@/i18n/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: '', company: '', role: '', email: '', phone: '', country: '', message: '' };

export default function Contacts() {
  const { t } = useLang();
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errMsg, setErrMsg] = useState('');

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrMsg('');
    try {
      await axios.post(`${API}/inquiries`, { ...form, inquiry_type: 'partner' });
      setStatus('success');
      setForm(emptyForm);
    } catch (err) {
      setStatus('error');
      setErrMsg(err?.response?.data?.detail?.[0]?.msg || t.contacts.error);
    }
  };

  return (
    <div data-testid="contacts-page">
      <section className="mm-section" style={{ paddingTop: 72 }}>
        <div className="container-mm">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 64 }}>
            <div>
              <div className="eyebrow">{t.nav.contacts}</div>
              <h1 style={{ fontSize: 'clamp(44px, 5.5vw, 84px)', marginTop: 16 }}>{t.contacts.title}</h1>
              <p style={{ fontSize: 17, color: 'var(--mm-slate)', marginTop: 20 }}>{t.contacts.lead}</p>

              <div style={{ marginTop: 40, display: 'flex', flexDirection: 'column', gap: 24 }}>
                <ContactRow icon={<MapPin size={18} />} label={t.contacts.address_title} value={t.contacts.address_value} />
                <ContactRow icon={<Phone size={18} />} label={t.contacts.phone_title} value="+992 11 811 17 86" href="tel:+992118111786" />
                <ContactRow icon={<Mail size={18} />} label={t.contacts.email_title} value="info@medmir.llc" href="mailto:info@medmir.llc" />
                <ContactRow icon={<Clock size={18} />} label={t.contacts.hours_title} value={t.contacts.hours_value} />
              </div>
            </div>

            <div>
              <form className="mm-form" onSubmit={onSubmit} data-testid="inquiry-form">
                <Field name="name" label={t.contacts.name} value={form.name} onChange={onChange} required />
                <Field name="company" label={t.contacts.company} value={form.company} onChange={onChange} required />
                <Field name="role" label={t.contacts.role} value={form.role} onChange={onChange} />
                <Field name="country" label={t.contacts.country} value={form.country} onChange={onChange} />
                <Field name="email" type="email" label={t.contacts.email} value={form.email} onChange={onChange} required />
                <Field name="phone" label={t.contacts.phone} value={form.phone} onChange={onChange} required />
                <div className="mm-field full">
                  <label>{t.contacts.message}</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    required
                    minLength={5}
                    data-testid="input-message"
                  />
                </div>

                {status === 'success' && (
                  <div className="mm-alert-success full" data-testid="form-success">{t.contacts.success}</div>
                )}
                {status === 'error' && (
                  <div className="mm-alert-success full" style={{ borderColor: '#B23A48', color: '#B23A48', background: 'rgba(178,58,72,0.06)' }} data-testid="form-error">
                    {errMsg}
                  </div>
                )}

                <div className="full" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-dark"
                    type="submit"
                    disabled={status === 'sending'}
                    data-testid="submit-inquiry"
                  >
                    {status === 'sending' ? t.contacts.submitting : t.contacts.submit}
                    <ArrowUpRight size={16} />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="mm-section alabaster" style={{ paddingTop: 0 }} data-testid="contacts-map-section">
        <div className="container-mm">
          <div className="mm-geo-visual" style={{ aspectRatio: '21/7', borderRadius: 4, overflow: 'hidden' }} data-testid="contacts-map">
            <iframe
              title="MED MIR — Dushanbe, Tajikistan office location"
              src="https://www.google.com/maps?q=Dushanbe,Tajikistan&z=13&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, display: 'block' }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ name, label, type = 'text', value, onChange, required }) {
  return (
    <div className="mm-field">
      <label htmlFor={`f-${name}`}>{label}</label>
      <input
        id={`f-${name}`}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={`input-${name}`}
      />
    </div>
  );
}

function ContactRow({ icon, label, value, href }) {
  const inner = (
    <>
      <div style={{
        width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid var(--mm-line)', borderRadius: 4, color: 'var(--mm-emerald)'
      }}>{icon}</div>
      <div>
        <div className="eyebrow" style={{ color: 'var(--mm-slate)' }}>{label}</div>
        <div style={{ fontSize: 18, marginTop: 4 }}>{value}</div>
      </div>
    </>
  );
  const style = { display: 'flex', gap: 16, alignItems: 'center', textDecoration: 'none', color: 'inherit' };
  return href ? <a href={href} style={style}>{inner}</a> : <div style={style}>{inner}</div>;
}
